import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Shield, CheckCircle, XCircle, AlertTriangle, Loader2 } from 'lucide-react';
import api from '@/services/api';

const InviteAcceptPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [status, setStatus] = useState('loading'); // loading, success, error, login_required
  const [message, setMessage] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [role, setRole] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Invalid invitation link. No token provided.');
      return;
    }

    const user = localStorage.getItem('user');
    if (!user) {
      setStatus('login_required');
      setMessage('Please log in to accept this invitation.');
      return;
    }

    acceptInvitation();
  }, [token]);

  const acceptInvitation = async () => {
    try {
      setStatus('loading');
      const response = await api.post(`/api/admin/invitations/${token}/accept`);
      setStatus('success');
      setTenantName(response.data.tenant_name || 'the organization');
      setRole(response.data.role || 'member');
      setMessage('You have successfully joined the organization!');
    } catch (error) {
      setStatus('error');
      const errorDetail = error.response?.data?.detail || 'Failed to accept invitation';
      setMessage(errorDetail);
    }
  };

  const handleLogin = () => {
    // Store the token to use after login
    localStorage.setItem('pendingInviteToken', token);
    navigate('/login');
  };

  const handleContinue = () => {
    navigate('/apps');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md" data-testid="invite-accept-card">
        <CardHeader className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-indigo-600/20 flex items-center justify-center">
            <Shield className="w-8 h-8 text-indigo-400" />
          </div>
          <CardTitle className="text-2xl">Team Invitation</CardTitle>
          <CardDescription>
            Beskar Security Platform
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {status === 'loading' && (
            <div className="text-center py-8">
              <Loader2 className="w-12 h-12 mx-auto text-indigo-500 animate-spin mb-4" />
              <p className="text-gray-500">Processing your invitation...</p>
            </div>
          )}
          
          {status === 'success' && (
            <div className="text-center py-4">
              <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
              <h3 className="text-xl font-semibold text-green-700 mb-2">Welcome!</h3>
              <p className="text-gray-600 mb-4">{message}</p>
              <div className="bg-green-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-green-800">
                  You have joined <strong>{tenantName}</strong> as a <strong className="capitalize">{role.replace('_', ' ')}</strong>.
                </p>
              </div>
              <Button 
                onClick={handleContinue}
                className="w-full bg-indigo-600 hover:bg-indigo-700"
                data-testid="continue-btn"
              >
                Continue to Dashboard
              </Button>
            </div>
          )}
          
          {status === 'error' && (
            <div className="text-center py-4">
              <XCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
              <h3 className="text-xl font-semibold text-red-700 mb-2">Unable to Accept</h3>
              <p className="text-gray-600 mb-6">{message}</p>
              <div className="space-y-2">
                <Button 
                  onClick={() => navigate('/apps')}
                  className="w-full"
                  variant="outline"
                >
                  Go to Dashboard
                </Button>
                <Button 
                  onClick={() => navigate('/login')}
                  variant="ghost"
                  className="w-full"
                >
                  Login with Different Account
                </Button>
              </div>
            </div>
          )}
          
          {status === 'login_required' && (
            <div className="text-center py-4">
              <AlertTriangle className="w-16 h-16 mx-auto text-amber-500 mb-4" />
              <h3 className="text-xl font-semibold text-amber-700 mb-2">Login Required</h3>
              <p className="text-gray-600 mb-6">{message}</p>
              <Button 
                onClick={handleLogin}
                className="w-full bg-indigo-600 hover:bg-indigo-700"
                data-testid="login-btn"
              >
                Login to Accept Invitation
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default InviteAcceptPage;
